import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import AgriPanel from "components/AgriTheme/AgriPanel";
import AgriStatCard from "components/AgriTheme/AgriStatCard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const getCurrentMonthValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const summaryCards = [
  {
    key: "total_app_open_users",
    label: "App Opens",
    icon: "phone_android",
    tone: "linear-gradient(135deg, #db2777 0%, #9d174d 100%)",
    helper: "Sum of users who opened the app across this month",
  },
  {
    key: "total_ai_mentor_users",
    label: "AI Mentor Opens",
    icon: "smart_toy",
    tone: "linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)",
    helper: "Sum of users who opened AI Mentor across this month",
  },
  {
    key: "total_daily_active_users",
    label: "Active Users",
    icon: "monitoring",
    tone: "linear-gradient(135deg, #395fa8 0%, #1d3f79 100%)",
    helper: "Sum of users who attempted tests across this month",
  },
  {
    key: "monthly_unique_active_users",
    label: "Monthly Unique Active",
    icon: "group_work",
    tone: "linear-gradient(135deg, #0891b2 0%, #164e63 100%)",
    helper: "Distinct users who opened the app this month",
  },
  {
    key: "total_practice_tests_solved",
    label: "Practice Tests",
    icon: "assignment_turned_in",
    tone: "linear-gradient(135deg, #2f7a42 0%, #183526 100%)",
    helper: "Practice tests solved across selected month",
  },
  {
    key: "total_users_onboarded",
    label: "Users Onboarded",
    icon: "person_add",
    tone: "linear-gradient(135deg, #dd8e2f 0%, #b86518 100%)",
    helper: "New users created in selected month",
  },
  {
    key: "total_mcqs_solved",
    label: "MCQs Solved",
    icon: "fact_check",
    tone: "linear-gradient(135deg, #8b3fa5 0%, #5a2374 100%)",
    helper: "Total MCQs solved in practice during the month",
  },
];

const chartSeries = [
  {
    key: "app_open_users",
    label: "App Opens",
    color: "#ff4db8",
    gradient: "linear-gradient(180deg, #ff7ad1 0%, #ff4db8 45%, #d61f88 100%)",
    glow: "0 12px 22px rgba(255,77,184,0.42)",
  },
  {
    key: "ai_mentor_users",
    label: "AI Mentor Opens",
    color: "#9b6bff",
    gradient: "linear-gradient(180deg, #bf9cff 0%, #9b6bff 45%, #6d3df2 100%)",
    glow: "0 12px 22px rgba(155,107,255,0.42)",
  },
  {
    key: "daily_active_users",
    label: "Active Users",
    color: "#39c6ff",
    gradient: "linear-gradient(180deg, #74ddff 0%, #39c6ff 45%, #0f8fe0 100%)",
    glow: "0 12px 22px rgba(57,198,255,0.4)",
  },
  {
    key: "practice_tests_solved",
    label: "Practice Tests Solved",
    color: "#37e27d",
    gradient: "linear-gradient(180deg, #7ff5ae 0%, #37e27d 45%, #18a957 100%)",
    glow: "0 12px 22px rgba(55,226,125,0.38)",
  },
  {
    key: "users_onboarded",
    label: "Users Onboarded",
    color: "#ffb020",
    gradient: "linear-gradient(180deg, #ffd266 0%, #ffb020 45%, #e07a00 100%)",
    glow: "0 12px 22px rgba(255,176,32,0.4)",
  },
];

const GRAPH_HEIGHT = 260;

function AdminAnalytics() {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue);
  const [analytics, setAnalytics] = useState({ days: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(false);
  const [notiColor, setNotiColor] = useState("warning");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (init() !== "success") {
      return undefined;
    }

    setLoading(true);
    axios
      .get(Constants.getUrls.getAdminMonthlyAnalytics, {
        params: {
          month: selectedMonth,
        },
      })
      .then((resp) => {
        setAnalytics(resp?.data?.data || { days: [], summary: {} });
      })
      .catch((err) => {
        setNotification(true);
        setNotiColor("warning");
        setMessage(err?.response?.data?.message || "Unable to load monthly analytics.");
      })
      .finally(() => {
        setLoading(false);
      });

    return undefined;
  }, [selectedMonth]);

  const chartData = useMemo(() => {
    const rows = analytics?.days || [];
    const maxValue = rows.reduce((currentMax, item) => {
      const nextMax = Math.max(
        Number(item.app_open_users || 0),
        Number(item.ai_mentor_users || 0),
        Number(item.daily_active_users || 0),
        Number(item.practice_tests_solved || 0),
        Number(item.users_onboarded || 0)
      );
      return Math.max(currentMax, nextMax);
    }, 0);

    return {
      rows,
      maxValue,
      yAxisTicks: Array.from({ length: 5 }, (_, index) => {
        const steps = 4;
        const value = Math.round((maxValue / steps) * (steps - index));
        return {
          value,
          top: (index / steps) * GRAPH_HEIGHT,
        };
      }),
    };
  }, [analytics]);

  return (
    <DashboardLayout>
      <DashboardNavbar title="Monthly Analytics" />

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: `repeat(${summaryCards.length}, 132px)`,
                lg: `repeat(${summaryCards.length}, minmax(0, 1fr))`,
              },
              gap: 1,
              overflowX: "auto",
              pb: 0.5,
            }}
          >
            {summaryCards.map((card) => (
              <AgriStatCard
                key={card.key}
                icon={card.icon}
                label={card.label}
                value={analytics?.summary?.[card.key] ?? 0}
                tone={card.tone}
                helper={card.helper}
                compact
                dense
              />
            ))}
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <AgriPanel
            title="Daily Monthly Breakdown"
            subtitle="Every day of the selected month is plotted separately for app opens, AI Mentor opens, active users, practice tests solved, and new user onboarding."
            actions={
              <TextField
                type="month"
                size="small"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                inputProps={{
                  max: getCurrentMonthValue(),
                }}
              />
            }
          >
            {loading ? (
              <MDTypography variant="h6">Loading monthly analytics...</MDTypography>
            ) : !(analytics?.days || []).length ? (
              <MDTypography variant="button" color="text">
                No analytics found for the selected month.
              </MDTypography>
            ) : (
              <MDBox>
                <MDBox
                  mb={2}
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="center"
                >
                  {chartSeries.map((series) => (
                    <MDBox
                      key={series.key}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.25}
                      py={0.85}
                      borderRadius="xl"
                      sx={{
                        background: "linear-gradient(180deg, rgba(18,38,60,0.9) 0%, rgba(12,26,43,0.94) 100%)",
                        border: "1px solid rgba(126, 170, 220, 0.16)",
                        boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
                      }}
                    >
                      <MDBox
                        width="12px"
                        height="12px"
                        borderRadius="999px"
                        sx={{
                          backgroundColor: series.color,
                          boxShadow: series.glow,
                        }}
                      />
                      <MDTypography variant="button" sx={{ color: "#d7e5ff", fontSize: "0.78rem" }}>
                        {series.label}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                <MDBox
                  display="flex"
                  gap={2}
                  sx={{
                    position: "relative",
                    minHeight: "24rem",
                    borderRadius: "24px",
                    background:
                      "linear-gradient(180deg, rgba(12,24,39,0.92) 0%, rgba(9,18,31,0.98) 100%)",
                    border: "1px solid rgba(126, 170, 220, 0.12)",
                    p: { xs: 1.5, md: 2 },
                    overflowX: "auto",
                    overflowY: "hidden",
                  }}
                >
                  <MDBox
                    sx={{
                      width: "54px",
                      minWidth: "54px",
                      height: `${GRAPH_HEIGHT + 32}px`,
                      position: "relative",
                    }}
                  >
                    {chartData.yAxisTicks.map((tick) => (
                      <MDBox
                        key={`tick-${tick.top}`}
                        sx={{
                          position: "absolute",
                          top: `${tick.top}px`,
                          right: 0,
                          transform: "translateY(-50%)",
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          sx={{ color: "#6f89a8", fontSize: "0.7rem", fontWeight: 700 }}
                        >
                          {tick.value}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>

                  <MDBox sx={{ minWidth: "max-content", flex: 1, position: "relative" }}>
                    {chartData.yAxisTicks.map((tick) => (
                      <MDBox
                        key={`grid-${tick.top}`}
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: `${tick.top}px`,
                          borderTop: "1px dashed rgba(143, 167, 196, 0.12)",
                          zIndex: 0,
                        }}
                      />
                    ))}

                    <MDBox
                      display="flex"
                      alignItems="flex-end"
                      gap={1.1}
                      sx={{
                        height: `${GRAPH_HEIGHT + 48}px`,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {chartData.rows.map((item) => {
                        const divisor = chartData.maxValue || 1;

                        return (
                          <MDBox
                            key={item.date}
                            minWidth="58px"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                          >
                            <MDBox
                              display="flex"
                              alignItems="flex-end"
                              justifyContent="center"
                              gap={0.45}
                              sx={{
                                height: `${GRAPH_HEIGHT}px`,
                                width: "100%",
                                borderRadius: "18px",
                                background:
                                  "linear-gradient(180deg, rgba(17,35,56,0.3) 0%, rgba(13,29,47,0.72) 100%)",
                                border: "1px solid rgba(126, 170, 220, 0.12)",
                                px: 0.75,
                                py: 0.9,
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                                transition: "transform 160ms ease, border-color 160ms ease",
                                "&:hover": {
                                  transform: "translateY(-3px)",
                                  borderColor: "rgba(126, 170, 220, 0.24)",
                                },
                              }}
                            >
                              {chartSeries.map((series) => {
                                const rawValue = Number(item[series.key] || 0);
                                const barHeight =
                                  rawValue > 0 ? Math.max((rawValue / divisor) * GRAPH_HEIGHT, 6) : 0;

                                return (
                                  <MDBox
                                    key={`${item.date}-${series.key}`}
                                    sx={{
                                      position: "relative",
                                      width: "8px",
                                      height: `${barHeight}px`,
                                      minHeight: rawValue > 0 ? "6px" : 0,
                                      borderRadius: "999px",
                                      background: series.gradient,
                                      boxShadow: series.glow,
                                      cursor: "pointer",
                                      transition: "transform 140ms ease, opacity 140ms ease",
                                      opacity: rawValue > 0 ? 1 : 0.16,
                                      border: rawValue > 0 ? "1px solid rgba(255,255,255,0.12)" : "none",
                                      "&:hover": {
                                        transform: "scaleY(1.04)",
                                      },
                                      "&:hover .graph-tooltip": {
                                        opacity: 1,
                                        transform: "translate(-50%, -8px)",
                                      },
                                    }}
                                  >
                                    <MDBox
                                      className="graph-tooltip"
                                      px={1}
                                      py={0.65}
                                      sx={{
                                        position: "absolute",
                                        left: "50%",
                                        bottom: "100%",
                                        mb: 0.75,
                                        minWidth: "max-content",
                                        borderRadius: "10px",
                                        background:
                                          "linear-gradient(180deg, rgba(10,20,34,0.98) 0%, rgba(6,14,24,0.98) 100%)",
                                        border: "1px solid rgba(126, 170, 220, 0.12)",
                                        color: "#fff",
                                        fontSize: "0.68rem",
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                        opacity: 0,
                                        pointerEvents: "none",
                                        transform: "translate(-50%, 0)",
                                        transition: "all 140ms ease",
                                        zIndex: 3,
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {series.label}: {rawValue}
                                    </MDBox>
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                            <MDBox
                              px={1}
                              py={0.45}
                              borderRadius="lg"
                              sx={{
                                background: "rgba(17,35,56,0.72)",
                                border: "1px solid rgba(126, 170, 220, 0.1)",
                              }}
                            >
                              <MDTypography
                                variant="caption"
                                sx={{ color: "#d7e5ff", fontWeight: 700, fontSize: "0.68rem" }}
                              >
                                {item.label}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            )}
          </AgriPanel>
        </Grid>

      </Grid>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default AdminAnalytics;
